// pkg77 — D-77b billing receipt on every summary run: billed → "$X billed to {client} — summary archived to their Billing tab"; unbilled → "firm paid"; per-run note under the panel (the stored ai_summary itself is run-agnostic) — delivered 2026-07-13
// pkg75 — per-submission "✦ AI summary · 1 credit" inline in the action row (pkg61 no-menu rule) opening an inline confirm (pkg57 picker precedent) with the cost-recovery opt-in ("Bill to {client} as an expense · $1.00", firm pays by default); stored result renders in a panel under the row (digest + Incomplete/Inconsistent flags, viewing free, Re-run costs, D-75a/b/c) — delivered 2026-07-13
// pkg62 — "Share to portal" renamed "Share to Client's Portal" (Ryan's verdict: the short form didn't say whose portal) — delivered 2026-07-10
// pkg61 — the ⋯ menu removed (Ryan's verdict): Edit Answers and Delete sit inline at the end of the action row like the old submissions page, no click to reveal — delivered 2026-07-10
// pkg60 — Documents lifecycle view (D-58e/f): ONE screen replaces Prepare and View Submissions — every assigned client plus every submission, a Draft → Prepared → Sent to client → Signed pipeline derived display-only from existing data (attorney_ready, submission, ack state, share state), the contextual next-step action per row, the embedded Prepare wizard (pkg50 responsible attorney + pkg46 Sign-for-the-firm preserved verbatim), embedded Edit Answers, pkg57 share/unshare, and the pkg41/46 signer audit line — delivered 2026-07-10
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import ModuleWizard from '../components/ModuleWizard';
import { moduleCompletedBy, hasOwnedFields } from '../utils/fieldOwnership';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const font = { fontFamily: 'Arial, sans-serif' };

// pkg41 — acknowledgment display state (copied verbatim from the old
// submissions page so the derivation never drifts):
//   module doesn't require ack           → null (no pill, no extra buttons)
//   staff submission (no client)         → null
//   ack exists, acknowledged_at >= submitted_at → 'acknowledged'
//   ack exists, acknowledged_at <  submitted_at → 'superseded'
//   no ack                                → 'awaiting'
function ackState(mod, sub) {
  if (!mod?.requires_acknowledgment) return null;
  if (!sub.client_id) return null; // internal staff fill — nothing to acknowledge
  if (!sub.ack_id) return 'awaiting';
  return new Date(sub.ack_acknowledged_at) >= new Date(sub.submitted_at) ? 'acknowledged' : 'superseded';
}

// pkg60 (D-58f) — the pipeline is DISPLAY-ONLY: every state below maps onto
// data the server already keeps. Nothing here writes anything.
//   Draft            — attorney-owned fields exist and aren't filled yet
//   Prepared         — attorney fields done (or none exist)
//   Sent to client   — it's live in the client's portal / awaiting their part
//   Signed|Submitted — acknowledged (ack modules) or submitted (the rest)
function deriveStages(mod, schema, client, sub) {
  const hasAttorney = mod.kind === 'assembly' && mod.audience === 'client' && hasOwnedFields(schema, 'attorney');
  const needsAck    = mod.requires_acknowledgment === true && mod.kind === 'assembly';
  const finalLabel  = needsAck ? 'Signed' : 'Submitted';
  const ackSt       = sub ? ackState(mod, sub) : null;

  // done = index of the last completed stage; current = the active one.
  let current = 2, subtext = '', warn = false;
  if (hasAttorney && client && client.attorney_ready === false) {
    current = 0;
    subtext = 'Your fields are blank — the client cannot finish until you prepare them.';
  } else if (!sub) {
    current = 2;
    subtext = hasAttorney && moduleCompletedBy(schema) === 'attorney'
      ? 'Prepared — waiting on the client to review.'
      : 'In their portal — nothing submitted yet.';
  } else if (needsAck && ackSt === 'awaiting') {
    current = 2;
    subtext = 'Answers are in — waiting on the client to review and sign.';
  } else if (needsAck && ackSt === 'superseded') {
    current = 2; warn = true;
    subtext = 'Signed copy superseded — the client changed answers after signing and needs to sign again.';
  } else {
    current = 3;
    subtext = '';
  }

  const labels = ['Draft', 'Prepared', 'Sent to client',
    current === 3 && sub
      ? `${finalLabel} ${new Date(needsAck && sub.ack_acknowledged_at ? sub.ack_acknowledged_at : sub.submitted_at).toLocaleDateString()}`
      : finalLabel];
  if (current === 0) labels[0] = 'Draft — needs your input';

  return {
    hasAttorney, needsAck, current, warn, subtext,
    stages: labels.map((label, i) => ({
      label,
      state: i < current ? 'done' : i === current ? 'current' : 'todo',
    })),
  };
}

function StageChips({ stages, warn }) {
  const base = { ...font, fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '2px 10px', whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {stages.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color: '#c6ccd6', fontSize: 11 }}>→</span>}
          {s.state === 'done' && (
            <span style={{ ...base, color: '#1a3a2a', border: '1px solid #4caf7d', background: '#fff' }}>✓ {s.label}</span>
          )}
          {s.state === 'current' && (
            <span style={{
              ...base,
              background: warn ? '#fff8e1' : i === 3 ? '#f0faf5' : '#eef4ff',
              color:      warn ? '#7c5200' : i === 3 ? '#1a3a2a' : '#1a3a5c',
              border: `1px solid ${warn ? '#f5c842' : i === 3 ? '#4caf7d' : '#9db8e8'}`,
            }}>{s.label}</span>
          )}
          {s.state === 'todo' && (
            <span style={{ ...base, fontWeight: 400, color: '#aaa', border: '1px solid #e2e6ee', background: '#fff' }}>{s.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function AdminFirmModuleDocumentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth(); // pkg50

  const [mod,      setMod]      = useState(null);
  const [clients,  setClients]  = useState([]);
  const [subs,     setSubs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const [downloading, setDownloading] = useState('');
  const [deleting,    setDeleting]    = useState('');
  const [sharing,     setSharing]     = useState('');   // pkg57
  const [pickingFor,  setPickingFor]  = useState(null); // pkg57 — submission id awaiting a client pick
  const [pickClients, setPickClients] = useState(null); // pkg57 — lazy client list
  const [pickedId,    setPickedId]    = useState('');   // pkg57

  // ── Embedded Prepare wizard state (ported from AdminFirmModulePreparePage) ──
  const [activeClient, setActiveClient] = useState(null);
  const [prepAnswers,  setPrepAnswers]  = useState({});
  const [prepLoading,  setPrepLoading]  = useState(false);
  const [prepSaving,   setPrepSaving]   = useState(false);
  const [signChecked,  setSignChecked]  = useState(false); // pkg46
  const [signName,     setSignName]     = useState('');    // pkg46

  // pkg50 (D-50) — the document's responsible attorney. Signing-eligible
  // users ARE the attorney (no picker); staff preparers pick from the firm's
  // owners/attorneys, preselected to the first owner.
  const amEligible = adminUser && (adminUser.role === 'owner' || adminUser.role === 'attorney');
  const [firmAttorneys,  setFirmAttorneys]  = useState([]);
  const [attorneyUserId, setAttorneyUserId] = useState('');

  useEffect(() => {
    if (amEligible) { setAttorneyUserId(adminUser.id); return; }
    adminApi.getAdminUsers()
      .then(users => {
        const eligible = (users || []).filter(u => u.role === 'owner' || u.role === 'attorney')
          .sort((a, b) => (a.role === b.role ? (a.name || '').localeCompare(b.name || '') : a.role === 'owner' ? -1 : 1));
        setFirmAttorneys(eligible);
        setAttorneyUserId(prev => prev || eligible[0]?.id || '');
      })
      .catch(() => {});
  }, [amEligible, adminUser]);

  // ── Embedded Edit Answers state (ported from AdminFirmModuleSubmissionsPage) ─
  const [editingSub, setEditingSub]   = useState(null);
  const [editAnswers, setEditAnswers] = useState({});
  const [savingEdit, setSavingEdit]   = useState(false);

  // pkg75 — per-submission AI summary + cost-recovery opt-in
  const [summarizing,  setSummarizing]  = useState('');   // submission id in flight
  const [sumErrors,    setSumErrors]    = useState({});   // { [subId]: message }
  const [sumNotes,     setSumNotes]     = useState({});   // pkg77 (D-77b) — { [subId]: billing receipt for the LAST run }
  const [confirmSumFor, setConfirmSumFor] = useState(null); // submission id showing the inline confirm row
  const [billSummary,   setBillSummary]   = useState(false); // bill-to-client checkbox
  const [billSumAmount, setBillSumAmount] = useState('1.00');

  function openSummaryConfirm(sub) {
    setConfirmSumFor(sub.id);
    setBillSummary(false);
    setBillSumAmount('1.00');
    setSumErrors(prev => ({ ...prev, [sub.id]: '' }));
    setSumNotes(prev => ({ ...prev, [sub.id]: '' })); // pkg77 — a new run gets a fresh receipt
  }

  async function handleSummarize(sub) {
    setSummarizing(sub.id);
    setSumErrors(prev => ({ ...prev, [sub.id]: '' }));
    try {
      const body = billSummary
        ? { bill_to_client: { enabled: true, amount_cents: Math.round(parseFloat(billSumAmount || '1') * 100) } }
        : {};
      const r = await adminApi.summarizeFirmModuleSubmission(id, sub.id, body);
      // Patch the stored summary into the row in place — no full reload.
      setSubs(prev => prev.map(s => s.id === sub.id
        ? { ...s, ai_summary: r.ai_summary, ai_summary_at: r.ai_summary_at }
        : s));
      setConfirmSumFor(null);
      // pkg77 (D-77b) — every run's result carries a billing receipt.
      if (r.bill_requested && !r.billed) {
        setSumErrors(prev => ({ ...prev, [sub.id]: 'Summary generated, but the client charge did not record — add the expense manually from the client\u2019s Billing tab.' }));
      } else if (r.bill_requested && r.billed) {
        setSumNotes(prev => ({ ...prev, [sub.id]: `Billed $${r.billed.amount.toFixed(2)} to ${sub.client_name || 'the client'} as an expense — this summary is archived to their Billing tab.` }));
      } else {
        setSumNotes(prev => ({ ...prev, [sub.id]: 'Firm paid — 1 credit used, nothing billed to the client.' }));
      }
    } catch (err) {
      setSumErrors(prev => ({ ...prev, [sub.id]: err.message }));
    } finally {
      setSummarizing('');
    }
  }

  const load = useCallback(() => {
    Promise.all([
      adminApi.getFirmModule(id),
      adminApi.getFirmModuleClients(id).catch(() => []),   // internal modules have no assignments
      adminApi.listFirmModuleSubmissions(id),
    ])
      .then(([m, cls, ss]) => {
        setMod(m);
        setClients(Array.isArray(cls) ? cls : []);
        setSubs(Array.isArray(ss) ? ss : []);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleDownload(sub) {
    setDownloading(`doc-${sub.id}`); setError('');
    try {
      const blob = await adminApi.downloadFirmModuleSubmission(id, sub.id);
      const safeClient = String(sub.client_name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
      const safeMod    = String(mod?.label || 'Module').replace(/[^a-zA-Z0-9]/g, '_');
      saveBlob(blob, `${safeMod}_${safeClient}.docx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading('');
    }
  }

  // pkg41 — the FROZEN SNAPSHOT the client signed, byte-for-byte. NOT a
  // fresh render: template edits or answer changes after signing never
  // change what this returns.
  async function handleDownloadAcknowledged(sub) {
    setDownloading(`ack-${sub.id}`); setError('');
    try {
      const blob = await adminApi.downloadAcknowledgedCopy(id, sub.id);
      const safeClient = String(sub.client_name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
      const safeMod    = String(mod?.label || 'Module').replace(/[^a-zA-Z0-9]/g, '_');
      saveBlob(blob, `${safeMod}_${safeClient}_acknowledged.docx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading('');
    }
  }

  // pkg42 (D-41m) — warn-and-allow: the confirm names exactly what cascades.
  async function handleDelete(sub) {
    const hasSignedAck = !!sub.ack_id;
    const ok = window.confirm(
      `Delete this submission by ${sub.client_name}?\n\n` +
      '• The submitted answers are permanently removed.\n' +
      (hasSignedAck
        ? '• The SIGNED ACKNOWLEDGMENT RECORD attached to it — the frozen document copy, signature, and audit trail — is PERMANENTLY DESTROYED. Download the Acknowledged Copy and Certificate first if you need them.\n'
        : '') +
      '• The client can fill this module out fresh afterwards.\n\n' +
      'This cannot be undone.'
    );
    if (!ok) return;
    setDeleting(sub.id); setError('');
    try {
      await adminApi.deleteFirmModuleSubmission(id, sub.id);
      setSubs(prev => prev.filter(s => s.id !== sub.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting('');
    }
  }

  // pkg57 (D-56) — share / unshare. A submission with its own client (or a
  // linked one) shares in one click; otherwise a picker appears inline.
  async function doShare(sub, clientId) {
    setSharing(sub.id); setError('');
    try {
      const r = await adminApi.shareFirmModuleSubmission(id, sub.id, clientId || null);
      setSubs(prev => prev.map(s => s.id === sub.id
        ? { ...s, shared_with_client_id: r.shared_with_client_id, shared_with_client_name: r.shared_with_client_name, shared_at: r.shared_at }
        : s));
      setPickingFor(null); setPickedId('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSharing('');
    }
  }

  async function handleShareClick(sub) {
    if (sub.client_id || sub.linked_client_id) return doShare(sub, null);
    setPickingFor(sub.id); setPickedId('');
    if (!pickClients) {
      try {
        const rows = await adminApi.listClients();
        setPickClients((rows || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) { setError(err.message); }
    }
  }

  async function handleUnshare(sub) {
    setSharing(sub.id); setError('');
    try {
      await adminApi.unshareFirmModuleSubmission(id, sub.id);
      setSubs(prev => prev.map(s => s.id === sub.id
        ? { ...s, shared_with_client_id: null, shared_with_client_name: null, shared_at: null }
        : s));
    } catch (err) {
      setError(err.message);
    } finally {
      setSharing('');
    }
  }

  // ── Embedded Prepare (ported 1:1 from the old Prepare page) ────────────────
  async function openPrepare(clientRow) {
    setActiveClient(clientRow); setPrepAnswers({}); setError('');
    setSignChecked(false); setSignName(''); // pkg46
    setPrepLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const r = await adminApi.getPrepare(id, clientRow.id);
      setPrepAnswers(r.answers && typeof r.answers === 'object' ? r.answers : {});
    } catch (err) {
      setError(err.message);
      setActiveClient(null);
    } finally {
      setPrepLoading(false);
    }
  }

  async function handlePrepareSave() {
    setPrepSaving(true); setError('');
    try {
      await adminApi.savePrepare(id, activeClient.id, prepAnswers,
        signChecked && signName.trim() ? { typed_name: signName.trim() } : null, // pkg46
        attorneyUserId || null); // pkg50
      setActiveClient(null);
      load(); // refresh the pipeline after every save
    } catch (err) {
      setError(err.message);
    } finally {
      setPrepSaving(false);
    }
  }

  // ── Embedded Edit Answers (ported 1:1 from the old Submissions page) ───────
  function openEdit(sub) {
    setEditingSub(sub);
    setEditAnswers(sub.answers && typeof sub.answers === 'object' ? { ...sub.answers } : {});
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSaveEdit() {
    setSavingEdit(true); setError('');
    try {
      await adminApi.updateFirmModuleSubmission(id, editingSub.id, editAnswers);
      setSubs(prev => prev.map(s => s.id === editingSub.id ? { ...s, answers: editAnswers } : s));
      setEditingSub(null);
      load(); // ack state can flip to superseded server-side
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  }

  const btnStyle = (variant = 'default') => ({
    fontSize: 13, ...font, cursor: 'pointer',
    padding: '6px 14px', borderRadius: 4, border: 'none',
    background: variant === 'primary' ? '#1a3a2a' : '#eef2f7',
    color: variant === 'primary' ? '#fff' : '#1a3a2a',
    fontWeight: 600,
  });

  if (loading) return <div style={{ ...font, color: '#888', padding: 30 }}>Loading…</div>;
  if (!mod) return <div style={{ ...font, color: '#c00', padding: 30 }}>{error || 'Module not found.'}</div>;

  const schema = mod.schema_json || { sections: [] };
  const attorneyOnly = moduleCompletedBy(schema) === 'attorney';
  const hasAttorney = mod.kind === 'assembly' && mod.audience === 'client' && hasOwnedFields(schema, 'attorney');

  // ── Prepare wizard view ─────────────────────────────────────────────────────
  if (activeClient) {
    return (
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <div style={{ marginBottom: 6 }}>
          <button
            onClick={() => setActiveClient(null)}
            style={{ ...font, fontSize: 13, background: 'none', border: 'none', color: '#1a3a2a', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            ← Back to documents
          </button>
        </div>
        <h1 className="page-title" style={{ marginBottom: 2 }}>
          Prepare: {activeClient.name}
        </h1>
        <p style={{ ...font, fontSize: 13, color: '#555', marginTop: 0, marginBottom: 14 }}>
          {mod.label} — your fields only.{' '}
          {attorneyOnly
            ? 'Saving finalizes the document for this client; their portal will show it for review.'
            : "The client fills their own fields in their portal; your values are saved alongside theirs."}
        </p>
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, ...font, fontSize: 13, color: '#b91c1c' }}>
            {error}
          </div>
        )}
        {prepLoading ? (
          <div style={{ ...font, color: '#888', padding: 20 }}>Loading…</div>
        ) : (
          <>
          {/* pkg50 (D-50) — who this document names as the firm's attorney. */}
          <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: '10px 14px', marginBottom: 10, ...font, fontSize: 13 }}>
            {amEligible ? (
              <span>Attorney for this document: <strong>{adminUser.name}</strong>{adminUser.bar_number ? ` (${adminUser.bar_number})` : ''}</span>
            ) : (
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>Attorney for this document:</span>
                <select value={attorneyUserId} onChange={e => setAttorneyUserId(e.target.value)}
                  style={{ ...font, fontSize: 13, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}>
                  {!firmAttorneys.length && <option value="">— No attorneys found —</option>}
                  {firmAttorneys.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.bar_number ? ` (${a.bar_number})` : ''}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {/* pkg46 (D-46a) — sign for the firm. Optional; recorded with the
              save when checked, shown on the Certificate of Acknowledgment. */}
          <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', ...font, fontSize: 13, lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={signChecked} onChange={e => setSignChecked(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }} />
              <span><strong>Sign for the firm.</strong> I intend my typed name to serve as my electronic signature on this document. Recorded with the date, time, and IP address, and shown on the Certificate of Acknowledgment.</span>
            </label>
            {signChecked && (
              <input
                type="text"
                value={signName}
                onChange={e => setSignName(e.target.value)}
                placeholder="Your full name"
                maxLength={200}
                style={{ marginTop: 10, width: '100%', maxWidth: 320, boxSizing: 'border-box', padding: '8px 11px', fontSize: 14, border: '1px solid #ccc', borderRadius: 4, ...font }}
              />
            )}
          </div>
          <ModuleWizard
            schema={schema}
            answers={prepAnswers}
            onChange={(fieldId, val) => setPrepAnswers(prev => ({ ...prev, [fieldId]: val }))}
            onSubmit={handlePrepareSave}
            submitting={prepSaving}
            submitLabel={attorneyOnly ? (signChecked ? 'Sign & Finalize' : 'Save & Finalize') : (signChecked ? 'Sign & Save' : 'Save Attorney Fields')}
            ownerFilter="attorney"
          />
          </>
        )}
      </div>
    );
  }

  // ── Edit Answers view ───────────────────────────────────────────────────────
  if (editingSub) {
    return (
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ marginBottom: 6 }}>
          <button
            onClick={() => setEditingSub(null)}
            style={{ ...font, fontSize: 13, background: 'none', border: 'none', color: '#1a3a2a', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            ← Back to documents
          </button>
        </div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>
          Edit Answers: {editingSub.client_name}
        </h1>
        <p style={{ ...font, fontSize: 13, color: '#666', marginBottom: 8 }}>
          {mod?.label} · submitted {new Date(editingSub.submitted_at).toLocaleString()}
        </p>
        {editingSub.ack_id && (
          <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 4, padding: '8px 14px', marginBottom: 16, ...font, fontSize: 12, color: '#555' }}>
            This submission has a signed acknowledgment. Your edits do not change the signed record — the client's
            frozen copy stands as signed — but fresh document downloads will reflect the edited answers. Ask the
            client to re-submit if a correction is material enough to warrant a fresh signature.
          </div>
        )}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, ...font, fontSize: 13, color: '#b91c1c' }}>
            {error}
          </div>
        )}
        <ModuleWizard
          schema={mod?.schema_json || { sections: [] }}
          answers={editAnswers}
          onChange={(fieldId, val) => setEditAnswers(prev => ({ ...prev, [fieldId]: val }))}
          onSubmit={handleSaveEdit}
          submitting={savingEdit}
          submitLabel="Save Answers"
        />
      </div>
    );
  }

  // ── Row assembly: every assigned client + every submission ─────────────────
  const subsByClient = new Map(subs.filter(s => s.client_id).map(s => [s.client_id, s]));
  const clientIds    = new Set(clients.map(c => c.id));
  const rows = [
    ...clients.map(c => ({ key: `c-${c.id}`, client: c, sub: subsByClient.get(c.id) || null })),
    ...subs
      .filter(s => !s.client_id || !clientIds.has(s.client_id))
      .map(s => ({ key: `s-${s.id}`, client: null, sub: s })),
  ];
  const isClientPortal = mod.audience === 'client';

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: 6 }}>
        <button
          onClick={() => navigate('/admin/dashboard/firm-modules')}
          style={{ ...font, fontSize: 13, background: 'none', border: 'none', color: '#1a3a2a', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          ← Back to Document Modules
        </button>
      </div>

      <h1 className="page-title" style={{ marginBottom: 4 }}>
        Documents: {mod.label}
      </h1>
      <p style={{ ...font, fontSize: 13, color: '#666', marginBottom: 20 }}>
        {isClientPortal
          ? 'One row per client — the button is the next step.'
          : 'Staff fills of this internal module.'}
        {mod.requires_acknowledgment && ' Signed copies and certificates are available once a client signs.'}
      </p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, ...font, fontSize: 13, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {!rows.length && (
        <div style={{ ...font, color: '#888', padding: 24, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 6 }}>
          {isClientPortal
            ? 'No clients are assigned to this module yet. Assign clients from the Document Modules list first.'
            : 'No fills yet for this module.'}
        </div>
      )}

      {rows.map(({ key, client, sub }) => {
        const name  = client ? client.name  : sub.client_name;
        const email = client ? client.email : sub.client_email;
        const d = isClientPortal && client
          ? deriveStages(mod, schema, client, sub)
          : null; // submission-only and internal rows: no pipeline, just facts
        const hasAck = !!(sub && sub.ack_id);
        const signedState = sub ? ackState(mod, sub) : null;

        return (
          <div key={key} style={{
            background: '#fff', border: '1px solid #dde3ec', borderRadius: 6,
            padding: '14px 18px', marginBottom: 10,
          }}>
            {/* row header: name + the contextual next-step action(s) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ ...font, fontSize: 14, fontWeight: 700 }}>{name}</span>
                {email && <span style={{ ...font, fontSize: 12, color: '#888', marginLeft: 8 }}>{email}</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {d && d.current === 0 && (
                  <button style={btnStyle('primary')} onClick={() => openPrepare(client)}>
                    Prepare document
                  </button>
                )}
                {d && d.current === 2 && !sub && d.hasAttorney && (
                  <button style={btnStyle()} onClick={() => openPrepare(client)}>
                    Edit my fields
                  </button>
                )}
                {sub && (
                  <>
                    <button
                      style={btnStyle('primary')}
                      disabled={downloading === `doc-${sub.id}`}
                      onClick={() => handleDownload(sub)}
                    >
                      {downloading === `doc-${sub.id}` ? 'Downloading…' : '↓ Word doc'}
                    </button>
                    {hasAck && (
                      <button
                        style={btnStyle()}
                        disabled={downloading === `ack-${sub.id}`}
                        onClick={() => handleDownloadAcknowledged(sub)}
                        title="One file: the exact document as the client signed it, with the Certificate of Acknowledgment as the final page"
                      >
                        {downloading === `ack-${sub.id}` ? 'Downloading…' : '🔒 Signed copy'}
                      </button>
                    )}
                    {mod.kind === 'assembly' && !sub.shared_with_client_id && (
                      <button
                        style={btnStyle()}
                        disabled={sharing === sub.id}
                        onClick={() => handleShareClick(sub)}
                      >
                        {sharing === sub.id ? 'Sharing…' : "Share to Client's Portal"}
                      </button>
                    )}
                    {sub.shared_with_client_id && (
                      <button
                        style={{ ...btnStyle(), background: '#fff', border: '1px solid #c9d4e3', color: '#555' }}
                        disabled={sharing === sub.id}
                        onClick={() => handleUnshare(sub)}
                        title="Remove this document from the client's portal"
                      >
                        {sharing === sub.id ? '…' : 'Unshare'}
                      </button>
                    )}
                    {/* pkg61 — inline, no menu: same buttons the old page had */}
                    <button style={btnStyle()} onClick={() => openEdit(sub)}>
                      Edit Answers
                    </button>
                    {/* pkg75 — opens the inline confirm row (billing opt-in) */}
                    {!sub.ai_summary && (
                      <button
                        style={{ ...btnStyle(), border: '1px solid #1E2761', color: '#1E2761' }}
                        disabled={summarizing === sub.id}
                        onClick={() => openSummaryConfirm(sub)}
                        title="AI digest of this submission's answers, with incomplete/inconsistent flags. Costs 1 credit only if it succeeds."
                      >
                        {summarizing === sub.id ? 'Summarizing…' : '✦ AI summary · 1 credit'}
                      </button>
                    )}
                    <button
                      style={{ ...btnStyle(), background: '#fdf0f0', color: '#a02020' }}
                      disabled={deleting === sub.id}
                      onClick={() => handleDelete(sub)}
                    >
                      {deleting === sub.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* pipeline (client-portal client rows) or a plain fact line */}
            {d ? (
              <div style={{ marginTop: 10 }}>
                <StageChips stages={d.stages} warn={d.warn} />
                {d.subtext && (
                  <div style={{ ...font, fontSize: 12, color: d.warn ? '#7c5200' : '#888', marginTop: 6 }}>
                    {d.subtext}
                  </div>
                )}
              </div>
            ) : sub && (
              <div style={{ ...font, fontSize: 12, color: '#888', marginTop: 8 }}>
                Submitted {new Date(sub.submitted_at).toLocaleString()}
                {signedState === 'acknowledged' && ` · ✓ acknowledged ${new Date(sub.ack_acknowledged_at).toLocaleDateString()}`}
                {signedState === 'superseded' && ' · superseded — re-submitted after signing'}
              </div>
            )}

            {/* pkg57 — shared status + inline client picker */}
            {sub && sub.shared_with_client_id && (
              <div style={{ marginTop: 8, ...font, fontSize: 12, color: '#1a3a2a' }}>
                ✓ Shared with <strong>{sub.shared_with_client_name || 'client'}</strong>
                {sub.shared_at && ` — in their portal since ${new Date(sub.shared_at).toLocaleDateString()}`}
              </div>
            )}
            {sub && pickingFor === sub.id && !sub.shared_with_client_id && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', ...font, fontSize: 13 }}>
                <span>Share with:</span>
                {!pickClients ? (
                  <span style={{ color: '#888', fontSize: 12 }}>Loading clients…</span>
                ) : (
                  <>
                    <select value={pickedId} onChange={e => setPickedId(e.target.value)}
                      style={{ padding: '6px 8px', fontSize: 13, ...font, border: '1px solid #ccc', borderRadius: 4, maxWidth: 280 }}>
                      <option value="">— Pick a client —</option>
                      {pickClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button style={btnStyle('primary')} disabled={!pickedId || sharing === sub.id} onClick={() => doShare(sub, pickedId)}>
                      Share
                    </button>
                    <button style={btnStyle()} onClick={() => { setPickingFor(null); setPickedId(''); }}>Cancel</button>
                  </>
                )}
              </div>
            )}

            {/* pkg41 — signer audit line */}
            {hasAck && (
              <div style={{
                marginTop: 10, background: '#f8fafb', borderRadius: 4, padding: '8px 12px',
                ...font, fontSize: 12, color: '#555',
                display: 'flex', gap: 18, flexWrap: 'wrap',
              }}>
                <span>Signed as <strong style={{ color: '#222' }}>{sub.ack_typed_name}</strong></span>
                {sub.attorney_signature?.typed_name && ( /* pkg46 */
                  <span>Firm signed by <strong style={{ color: '#222' }}>{sub.attorney_signature.typed_name}</strong></span>
                )}
                <span>{new Date(sub.ack_acknowledged_at).toLocaleString()}</span>
                <span>IP {sub.ack_ip_address || '—'}</span>
                <span>Render: {sub.ack_render_path === 'exact_fill' ? 'exact-fill' : 'rebuilt'}</span>
              </div>
            )}

            {/* pkg75 — stored AI summary: viewing is free, Re-run costs */}
            {sub && sumErrors[sub.id] && (
              <div style={{ marginTop: 8, ...font, fontSize: 12, color: '#a02020' }}>{sumErrors[sub.id]}</div>
            )}
            {/* pkg77 (D-77b) — billing receipt for the run just completed */}
            {sub && sumNotes[sub.id] && (
              <div style={{ marginTop: 8, ...font, fontSize: 12, color: '#1E2761' }}>{sumNotes[sub.id]}</div>
            )}
            {sub && confirmSumFor === sub.id && (
              <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', ...font, fontSize: 13,
                background: '#f8fafb', borderRadius: 4, padding: '8px 12px' }}>
                <span style={{ color: '#555' }}>Uses <strong>1 AI credit</strong> — only if it succeeds.</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: (sub.client_id || sub.linked_client_id) ? 'pointer' : 'default',
                  color: (sub.client_id || sub.linked_client_id) ? '#333' : '#aaa' }}
                  title={(sub.client_id || sub.linked_client_id) ? 'Adds a billable expense to this client — it will appear on their next invoice.' : 'No client on this submission — the firm pays.'}>
                  <input type="checkbox" checked={billSummary}
                    disabled={!(sub.client_id || sub.linked_client_id)}
                    onChange={e => setBillSummary(e.target.checked)} />
                  Bill to {name} as an expense · $
                  <input type="text" value={billSumAmount} disabled={!billSummary}
                    onChange={e => setBillSumAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    style={{ width: 52, padding: '3px 6px', fontSize: 13, ...font, border: '1px solid #ccc', borderRadius: 4 }} />
                </label>
                <button style={btnStyle('primary')} disabled={summarizing === sub.id} onClick={() => handleSummarize(sub)}>
                  {summarizing === sub.id ? 'Summarizing…' : 'Generate'}
                </button>
                <button style={btnStyle()} disabled={summarizing === sub.id} onClick={() => setConfirmSumFor(null)}>Cancel</button>
              </div>
            )}
            {sub && sub.ai_summary && (
              <div style={{ marginTop: 10, background: '#f8fafb', borderRadius: 4, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ ...font, fontSize: 11, color: '#888' }}>
                    ✦ AI summary · generated {sub.ai_summary_at ? new Date(sub.ai_summary_at).toLocaleString() : '—'} · viewing is free
                  </span>
                  {confirmSumFor !== sub.id && (
                    <button
                      style={{ ...btnStyle(), padding: '2px 10px', fontSize: 11 }}
                      disabled={summarizing === sub.id}
                      onClick={() => openSummaryConfirm(sub)}
                      title="Generate a fresh summary (answers may have changed). Costs 1 credit only if it succeeds."
                    >
                      {summarizing === sub.id ? 'Summarizing…' : 'Re-run · 1 credit'}
                    </button>
                  )}
                </div>
                <div style={{ ...font, fontSize: 13, color: '#333', lineHeight: 1.55, marginTop: 6 }}>
                  {sub.ai_summary.digest}
                </div>
                {Array.isArray(sub.ai_summary.flags) && sub.ai_summary.flags.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: '1px solid #e4e9f0', paddingTop: 8 }}>
                    {sub.ai_summary.flags.map((fl, i) => (
                      <div key={i} style={{ ...font, fontSize: 12, color: '#444', marginTop: i ? 4 : 0 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 9,
                          background: '#fdf6e6', color: '#7c5200', border: '1px solid #7c520033',
                          marginRight: 8, textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                          {fl.type === 'incomplete' ? 'Incomplete' : 'Inconsistent'}
                        </span>
                        {fl.text}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ ...font, fontSize: 10, color: '#aab', marginTop: 8 }}>
                  AI-generated from the client's answers — verify against the submission before relying on it.
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* pkg60 — the drafts note: internal fills happen on the Fill Out page */}
      {mod.audience === 'internal' && (
        <div style={{ ...font, fontSize: 12, color: '#888', marginTop: 4 }}>
          Staff fill this module from the Document Modules list ("Fill Out"); completed fills appear here.
        </div>
      )}
    </div>
  );
}
